/**
 * Stripe configuration and utilities
 */

// Get publishable key from environment or use default
export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51P...';

// Subscription tiers
export const SUBSCRIPTION_TIERS = {
  COACH_FREE: {
    id: 'COACH_FREE',
    name: 'Free',
    description: 'Basic access to athlete profiles',
    features: [
      'View athlete profiles',
      'Basic CacheScore™',
      'Limited access',
    ],
    pricing: {
      annual: 0,
    },
    icon: 'database',
    // Feature access control - Free tier
    access: {
      cacheScore: true,
      cacheValue: false,  // Locked - requires Core
      cacheShare: false,  // Locked - requires Core
      cacheNILx: false,   // Locked - requires Pro
      athleteProfiles: true,
      filterSort: true,
      exportData: false,
    },
  },
  ATHLETE_PREMIUM: {
    id: 'ATHLETE_PREMIUM',
    name: 'Athlete Premium',
    description: 'Unlock advanced analytics and insights',
    features: [
      'CacheValue™ - Economic valuation',
      'CacheNILx™ - NIL deal benchmarking',
      'AI-powered summaries',
      'Brand matching',
      'See who viewed your profile',
    ],
    pricing: {
      monthly: 4.99,
      annual: 49.99,  // 17% savings
    },
    icon: '🏆',
  },
  COACH_CORE: {
    id: 'COACH_CORE',
    name: 'Core',
    description: 'Essential tools for smaller schools',
    features: [
      'Full athlete profiles',
      'CacheScore™ & CacheValue™',
      'CacheShare™ revenue modeling',
      'Filter and sort by metric',
      'Export athlete data',
    ],
    pricing: {
      monthly: 899.99,      // $900/mo
      quarterly: 2549.99,   // $850/mo ($150 savings vs monthly)
      annual: 10000.00,     // $833/mo ($800 savings vs monthly)
    },
    icon: 'database',
    // Feature access control
    access: {
      cacheScore: true,
      cacheValue: true,
      cacheShare: true,
      cacheNILx: false,
      athleteProfiles: true,
      filterSort: true,
      exportData: true,
    },
  },
  COACH_PRO: {
    id: 'COACH_PRO',
    name: 'Pro',
    description: 'Advanced analytics for competitive programs',
    features: [
      'Everything in Core',
      'CacheNILx™ benchmarking',
      'Advanced cohort analytics',
    ],
    pricing: {
      monthly: 1799.99,     // $1800/mo
      quarterly: 5099.99,   // $1700/mo ($300 savings vs monthly)
      annual: 20000.00,     // $1667/mo ($1600 savings vs monthly)
    },
    icon: 'trending-up',
    // Feature access control
    access: {
      cacheScore: true,
      cacheValue: true,
      cacheShare: true,
      cacheNILx: true,
      athleteProfiles: true,
      filterSort: true,
      exportData: true,
      advancedAnalytics: true,
    },
  },
  COACH_PREMIUM: {
    id: 'COACH_PREMIUM',
    name: 'Premium',
    description: 'Enterprise-grade insights and support (priced per sport)',
    features: [
      'Everything in Pro',
      'AI-powered insights by sport',
      'Advanced analytics modules',
      'Dedicated support',
      'Custom pricing per sport',
    ],
    pricing: {
      custom: true,
    },
    invoiceOnly: true,
    requiresApproval: true,
    icon: 'award',
    // Feature access control
    access: {
      cacheScore: true,
      cacheValue: true,
      cacheShare: true,
      cacheNILx: true,
      athleteProfiles: true,
      filterSort: true,
      exportData: true,
      advancedAnalytics: true,
      aiInsights: true,
      dedicatedSupport: true,
    },
  },
};

// Get tier details by ID
export const getTierDetails = (tierId) => {
  return SUBSCRIPTION_TIERS[tierId] || null;
};

// Check if tier is available for user's role
export const isTierAvailableForRole = (tierId, userPermissions) => {
  if (!userPermissions) return false;

  const tierToRoles = {
    ATHLETE_PREMIUM: ['IS_ATHLETE'],
    COACH_FREE: ['IS_COACH', 'IS_RECRUITER_SCOUT', 'IS_BRAND_AGENCY', 'IS_AD_PD'],
    COACH_CORE: ['IS_COACH', 'IS_RECRUITER_SCOUT', 'IS_BRAND_AGENCY', 'IS_AD_PD'],
    COACH_PRO: ['IS_COACH', 'IS_RECRUITER_SCOUT', 'IS_BRAND_AGENCY', 'IS_AD_PD'],
    COACH_PREMIUM: ['IS_COACH', 'IS_RECRUITER_SCOUT', 'IS_BRAND_AGENCY', 'IS_AD_PD'],
  };

  const allowedRoles = tierToRoles[tierId] || [];
  return allowedRoles.some(role => userPermissions.includes(role));
};

// Feature access utility functions
export const hasFeatureAccess = (subscriptionTier, featureName) => {
  // Default to free tier if no subscription
  const tier = subscriptionTier || 'COACH_FREE';
  const tierConfig = SUBSCRIPTION_TIERS[tier.toUpperCase()];

  if (!tierConfig || !tierConfig.access) {
    // If tier not found, assume free tier access
    return SUBSCRIPTION_TIERS.COACH_FREE.access[featureName] || false;
  }

  return tierConfig.access[featureName] || false;
};

// Get the tier required for a specific feature
export const getRequiredTierForFeature = (featureName) => {
  const featureToTier = {
    cacheScore: 'COACH_FREE',
    cacheValue: 'COACH_CORE',
    cacheShare: 'COACH_PRO',
    cacheNILx: 'COACH_PRO',
    athleteProfiles: 'COACH_FREE',
    filterSort: 'COACH_FREE',
    exportData: 'COACH_CORE',
    advancedAnalytics: 'COACH_PRO',
    aiInsights: 'COACH_PREMIUM',
    dedicatedSupport: 'COACH_PREMIUM',
  };

  return featureToTier[featureName] || 'COACH_PRO';
};

// Get upgrade message for locked feature
export const getUpgradeMessage = (featureName, currentTier) => {
  const requiredTier = getRequiredTierForFeature(featureName);
  const tierConfig = SUBSCRIPTION_TIERS[requiredTier];

  const featureDisplayNames = {
    cacheValue: 'CacheValue™',
    cacheShare: 'CacheShare™',
    cacheNILx: 'CacheNILx™',
    exportData: 'Export Data',
    advancedAnalytics: 'Advanced Analytics',
    aiInsights: 'AI Insights',
  };

  const featureDisplay = featureDisplayNames[featureName] || featureName;

  return {
    title: `Unlock ${featureDisplay}`,
    message: `Upgrade to ${tierConfig.name} to access ${featureDisplay}`,
    requiredTier: requiredTier,
    tierName: tierConfig.name,
  };
};
